/**
 * Apollo link that injects the demo-flex POV team into the
 * `x-pov-team-id` request header on every outgoing GraphQL operation.
 *
 * Replaces the per-operation `teamId` arg pattern that the demo-flex feature
 * (SE-4730 family) bolted onto ~25 GraphQL operations. POV is request-scoped
 * context — analogous to the auth user — not a per-field query argument, so
 * a header is the correct transport (matches `@PovTeamId()` on the BE).
 *
 * Behavior:
 * - Once the registry is ready (`hasSynced` && `isLoaded`): attach the header
 *   inline (or omit it when no team is selected) and forward synchronously.
 *   Hidden-mode orgs always have `activeTeamId === null`, so this path is a
 *   no-op header-wise for every real customer.
 * - Before the registry is ready: defer the forward until
 *   {@link TeamSelectionProvider} has reported in AND finished hydrating from
 *   AsyncStorage. This covers two cases that must not leak a header-less
 *   request: (1) the provider has not pushed its first snapshot yet — on a
 *   hard refresh the registry's `hidden` default is a placeholder, not the
 *   org's real mode (SE-4982); (2) a demo org's selection is still
 *   rehydrating. In both cases a header-less request would be served stale
 *   by the cache once the real POV resolves.
 * - EXCEPT for the identity operations the registry's readiness is itself
 *   derived from — see {@link NEVER_DEFERRED_OPERATIONS}. Deferring those
 *   is a deadlock rather than a wait.
 *
 * The link reads its inputs from {@link getPovTeamHeaderState}, which the
 * provider keeps in sync via {@link setPovTeamHeaderState}. We can't pull
 * the value through React context because this link is constructed inside
 * `ApolloClientProvider`, which sits ABOVE `TeamSelectionProvider` in the
 * tree.
 * @module providers/povTeamHeaderLink
 */
import { ApolloLink } from "@apollo/client/core";
import { Observable } from "@apollo/client/utilities";

import {
  getPovTeamHeaderState,
  POV_TEAM_ID_HEADER,
  waitForPovTeamSelectionReady,
} from "@/providers/povTeamHeaderState";

/**
 * Subset of the Apollo `Operation` shape that {@link attachHeader} actually
 * touches. Defined as a named type so `attachHeader`'s JSDoc doesn't have
 * to enumerate inline-object members (which trips `jsdoc/require-param-description`).
 */
interface HeaderCarrier {
  setContext: (ctx: object) => void;
  getContext: () => object;
}

/**
 * Operations that must NEVER be deferred by this link (SE-7735).
 *
 * The registry becomes ready only once {@link TeamSelectionProvider} has
 * reported in, and it cannot report in until `useCurrentUser()` stops
 * returning `undefined` — which is to say, until `AuthMe` has answered. So
 * deferring `AuthMe` itself makes the link wait on its own precondition:
 * the identity query never reaches the network, the provider never syncs,
 * the registry never becomes ready, and every operation in the session
 * hangs. `AuthRefreshToken` is here for the same reason — the boot refresh
 * is upstream of the identity it would otherwise be queued behind.
 *
 * Exempting them costs nothing. Both are identity operations: the POV team
 * does not vary their response, so there is no stale-cache hazard of the
 * kind the deferral exists to prevent (SE-4982), and neither carries the
 * header today in any case.
 */
const NEVER_DEFERRED_OPERATIONS: ReadonlySet<string> = new Set([
  "AuthMe",
  "AuthRefreshToken",
]);

/**
 * Mutates the operation's context to attach the POV header. The header is
 * only written when `activeTeamId` is non-null; a null id means "no POV
 * active" and we send no header at all (the BE treats absent header as
 * null, same as it used to treat omitted `teamId` arg). Hidden-mode orgs
 * always pass `null` here, so they never carry the header.
 * @param operation - The Apollo operation to attach the header to
 * @param activeTeamId - The POV team UUID, or null to attach no header
 * @remarks `x-pov-team-id` is an IDENTITY header: it is part of
 * {@link buildBatchKey} (providers/batchTerminalLink.ts) so queries under
 * different POV teams are never coalesced into one batched HTTP request. Keep
 * the two in sync — a new tenant/identity header must also be added there.
 */
function attachHeader(
  operation: HeaderCarrier,
  activeTeamId: string | null
): void {
  if (activeTeamId === null) {
    return;
  }
  const context = operation.getContext() as {
    headers?: Record<string, string>;
  };
  operation.setContext({
    headers: {
      ...context.headers,
      [POV_TEAM_ID_HEADER]: activeTeamId,
    },
  });
}

/**
 * Builds the Apollo link. Exported as a factory so the link chain in
 * `ApolloClientProvider` constructs it once per client instance, and so
 * tests can build fresh instances per case.
 * @returns A new Apollo link instance
 */
export function createPovTeamHeaderLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const snapshot = getPovTeamHeaderState();

    // Registry is ready: the provider has reported in and selection has
    // finished hydrating. Attach (or skip) the header inline and forward
    // synchronously. This is the steady-state fast path for the whole
    // session after the first commit.
    if (snapshot.hasSynced && snapshot.isLoaded) {
      attachHeader(operation, snapshot.activeTeamId);
      return forward(operation);
    }

    // The identity operations the registry's own readiness depends on. See
    // NEVER_DEFERRED_OPERATIONS — waiting on them here is a deadlock, not a
    // wait.
    if (NEVER_DEFERRED_OPERATIONS.has(operation.operationName)) {
      attachHeader(operation, snapshot.activeTeamId);
      return forward(operation);
    }

    // Registry not ready: the provider has not pushed its first snapshot,
    // or a demo org's selection is still hydrating from AsyncStorage. Defer
    // the forward until `setPovTeamHeaderState` makes the registry ready,
    // then attach the header from the freshly-loaded snapshot. Without this
    // wait the request would be served header-less and then served stale
    // (org-default) by the cache once the real POV resolves (SE-4982).
    //
    // Apollo's Observable contract requires returning a teardown so that
    // an unsubscribe before the wait resolves cancels the pending forward
    // and prevents an orphan downstream subscription.
    return new Observable(observer => {
      // eslint-disable-next-line functional/no-let -- captured by closures below for cancellation handshake
      let cancelled = false;
      // eslint-disable-next-line functional/no-let -- assigned after the async wait resolves
      let downstream: { unsubscribe: () => void } | null = null;
      waitForPovTeamSelectionReady()
        .then(() => {
          if (cancelled) {
            return;
          }
          attachHeader(operation, getPovTeamHeaderState().activeTeamId);
          downstream = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(error => {
          if (!cancelled) {
            observer.error(error as Error);
          }
        });
      return () => {
        cancelled = true;
        downstream?.unsubscribe();
      };
    });
  });
}
